import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  ChevronDown,
  ChevronRight,
  ShoppingCart,
  FileText,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  AlertTriangle,
  Info,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { PermissionGuard } from '@components/PermissionGuard';
import { ConfirmDialog } from '@components/Dialog';
import { DateInputBR } from '@components/DateInputBR';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { companyService } from '../../services/companyService';
import { Company, formatCnpj } from '../../types/company';
import {
  PurchaseOrder,
  PurchaseOrderSearchModel,
  PurchaseOrderStatusSummary,
  PURCHASE_ORDER_STATUS_OPTIONS,
} from '../../types/purchaseOrder';
import { formatDateTimeBR } from '@pages/financial/financialUtils';
import {
  PURCHASE_ORDER_SORT_OPTIONS,
  PURCHASE_ORDER_SORT_STORAGE_KEY,
  getPageRange,
  isOrderNumberSearch,
  parsePurchaseOrderSort,
  purchaseOrderSortHint,
  purchaseOrderSortParams,
  quickNavOutcome,
  type PurchaseOrderSort,
} from './purchaseOrderListUtils';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDate = (dateStr?: string) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('pt-BR');
};

const getStatusBadge = (status: number) => {
  const opt = PURCHASE_ORDER_STATUS_OPTIONS.find(o => o.value === status);
  if (!opt) return <span className="text-slate-500">—</span>;
  const colors: Record<string, string> = {
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-emerald-100 text-emerald-700',
    red: 'bg-red-100 text-red-700',
    yellow: 'bg-amber-100 text-amber-700',
  };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[opt.color]}`}>
      {opt.label}
    </span>
  );
};

// ─── Component ───────────────────────────────────────────────────

export const PurchaseOrderListView: React.FC = () => {
  const navigate = useNavigate();

  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // Valor com debounce que de fato dispara a busca (o campo acima muda a cada tecla).
  const [searchTerm, setSearchTerm] = useState('');
  // Ordenação escolhida na tela, lembrada neste navegador. O padrão mantém os prioritários no
  // topo; "data de criação" lista todos pelo momento do cadastro.
  const [sort, setSort] = useState<PurchaseOrderSort>(() => {
    try {
      return parsePurchaseOrderSort(localStorage.getItem(PURCHASE_ORDER_SORT_STORAGE_KEY));
    } catch {
      return parsePurchaseOrderSort(null);
    }
  });

  // Filtros
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: undefined as number | undefined,
    is_active: undefined as boolean | undefined,
    is_priority: undefined as boolean | undefined,
    company_id: undefined as number | undefined,
    date_from: '',
    date_to: '',
  });

  // Lookups
  const [companies, setCompanies] = useState<Company[]>([]);

  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const pageSize = 20;

  // Contadores por situação da listagem inteira (vêm da API; antes contavam só a página corrente)
  const [summary, setSummary] = useState<PurchaseOrderStatusSummary | null>(null);
  const stats = summary ?? { open: 0, in_progress: 0, completed: 0, cancelled: 0 };

  // Delete
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Quick navigation
  const [quickNavValue, setQuickNavValue] = useState('');
  const [quickNavLoading, setQuickNavLoading] = useState(false);
  const [quickNavError, setQuickNavError] = useState<string | null>(null);
  const [quickNavInfo, setQuickNavInfo] = useState<string | null>(null);

  // ── Load ──────────────────────────────────────────────────────

  // Cada carga ganha um número; a resposta de uma carga antiga (página ou busca que já mudou) é
  // descartada, senão uma requisição lenta sobrescreveria a página que está na tela.
  const requestSeq = useRef(0);

  const loadOrders = useCallback(async () => {
    const seq = ++requestSeq.current;
    try {
      setLoading(true);
      setError(null);

      const searchFilters: PurchaseOrderSearchModel = {
        page: currentPage,
        pageSize,
        searchName: searchTerm || undefined,
        ...purchaseOrderSortParams(sort),
        status: filters.status,
        is_active: filters.is_active,
        is_priority: filters.is_priority,
        company_id: filters.company_id,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
      };

      const response = await purchaseOrderService.getPurchaseOrders(searchFilters);
      if (seq !== requestSeq.current) return;
      setOrders(response.data);
      setTotalOrders(response.pagination.total);
      setTotalPages(response.pagination.totalPages);
      setSummary(response.summary ?? null);

      // A página pedida deixou de existir (ex.: excluiu o último pedido da última página).
      if (currentPage > response.pagination.totalPages) setCurrentPage(response.pagination.totalPages);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err instanceof Error ? err.message : 'Erro ao carregar pedidos de compra');
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [currentPage, filters, searchTerm, sort]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // Carrega empresas para o filtro
  useEffect(() => {
    companyService.getCompanies().then(setCompanies).catch(() => {});
  }, []);

  // Debounce da busca: dispara 500ms depois da última tecla, sempre a partir da página 1.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(search.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Filter dropdown outside-click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.filter-dropdown') && !target.closest('[data-filter-trigger]')) {
        setShowFilters(false);
      }
    };
    if (showFilters) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFilters]);

  // ── Delete ────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (deleteId === null) return;
    try {
      setDeleting(true);
      await purchaseOrderService.deletePurchaseOrder(deleteId);
      setDeleteId(null);
      loadOrders();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir pedido');
    } finally {
      setDeleting(false);
    }
  };

  const changeSort = (next: PurchaseOrderSort) => {
    setSort(next);
    setCurrentPage(1);
    try {
      localStorage.setItem(PURCHASE_ORDER_SORT_STORAGE_KEY, next);
    } catch {
      /* navegador sem storage: a escolha só não fica lembrada */
    }
  };

  // Mudar um filtro volta para a página 1: ficar na página 5 de um resultado que agora tem uma
  // página só mostraria a lista vazia.
  const updateFilters = (patch: Partial<typeof filters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    setCurrentPage(1);
  };

  // ── Active filter count ───────────────────────────────────────

  const activeFilterCount = [
    filters.status !== undefined,
    filters.is_active !== undefined,
    filters.is_priority !== undefined,
    filters.company_id !== undefined,
    !!filters.date_from,
    !!filters.date_to,
  ].filter(Boolean).length;

  // ── Quick Navigation ──────────────────────────────────────

  const handleQuickNav = async () => {
    const q = quickNavValue.trim();
    if (!q) return;
    try {
      setQuickNavLoading(true);
      setQuickNavError(null);
      setQuickNavInfo(null);
      const ids = await purchaseOrderService.lookupPurchaseOrder(q);
      const outcome = quickNavOutcome(ids);
      if (!outcome) {
        setQuickNavError('Pedido de compra não encontrado');
      } else if (outcome.kind === 'open') {
        navigate(`/purchase-orders/${outcome.id}`);
      } else {
        // Número ambíguo: filtra a listagem por ele e deixa a pessoa escolher.
        setSearch(q);
        setSearchTerm(q);
        setCurrentPage(1);
        setQuickNavInfo(`${outcome.ids.length} pedidos com o nº ${q} — escolha na lista`);
      }
    } catch (err) {
      setQuickNavError(err instanceof Error ? err.message : 'Pedido não encontrado');
    } finally {
      setQuickNavLoading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pedidos de Compra</h1>
          <p className="text-sm text-slate-500 mt-1">
            {totalOrders} pedido{totalOrders !== 1 ? 's' : ''} encontrado{totalOrders !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex items-center gap-2">
            {/* Quick navigation */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 h-9 shadow-sm">
              <input
                type="text"
                className="bg-transparent text-sm outline-none w-36 placeholder:text-slate-400"
                placeholder="Nº do pedido..."
                value={quickNavValue}
                onChange={e => { setQuickNavValue(e.target.value); setQuickNavError(null); setQuickNavInfo(null); }}
                onKeyDown={e => e.key === 'Enter' && handleQuickNav()}
              />
              <button
                className="text-slate-500 hover:text-emerald-600 disabled:opacity-40 flex items-center"
                onClick={handleQuickNav}
                disabled={quickNavLoading || !quickNavValue.trim()}
                title="Ir para o pedido de compra"
              >
                {quickNavLoading
                  ? <Loader2 size={15} className="animate-spin" />
                  : <ChevronRight size={15} />}
              </button>
            </div>
            <PermissionGuard permission="purchase_orders.create">
              <Button icon={<Plus size={20} />} onClick={() => navigate('/purchase-orders/new')}>
                Novo Pedido
              </Button>
            </PermissionGuard>
          </div>
          {quickNavError && (
            <p className="text-xs text-red-500">{quickNavError}</p>
          )}
          {quickNavInfo && (
            <p className="text-xs text-amber-600">{quickNavInfo}</p>
          )}
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Em Aberto</p>
              <p className="text-lg font-bold text-slate-900">{stats.open}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Em Andamento</p>
              <p className="text-lg font-bold text-slate-900">{stats.in_progress}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Atendidos</p>
              <p className="text-lg font-bold text-slate-900">{stats.completed}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-3 px-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
              <XCircle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Cancelados</p>
              <p className="text-lg font-bold text-slate-900">{stats.cancelled}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & filters */}
      <Card>
        <CardContent className="py-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                className="input pl-10 w-full"
                placeholder="Buscar por nº pedido, referência ou fornecedor..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="relative">
              <Button
                variant="outline"
                data-filter-trigger
                icon={<Filter size={18} />}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filtros
                {activeFilterCount > 0 && (
                  <span className="ml-1.5 bg-emerald-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
                <ChevronDown size={16} className="ml-1" />
              </Button>

              {showFilters && (
                <div className="filter-dropdown absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-lg border border-slate-200 p-4 z-30 space-y-4">
                  {/* Status */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Situação</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.status !== undefined ? filters.status : ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateFilters({ status: val === '' ? undefined : Number(val) });
                      }}
                    >
                      <option value="">Todas</option>
                      {PURCHASE_ORDER_STATUS_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Ativo */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Status</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.is_active !== undefined ? String(filters.is_active) : ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateFilters({ is_active: val === '' ? undefined : val === 'true' });
                      }}
                    >
                      <option value="">Todos</option>
                      <option value="true">Ativos</option>
                      <option value="false">Inativos</option>
                    </select>
                  </div>

                  {/* Prioridade */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Prioridade</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.is_priority !== undefined ? String(filters.is_priority) : ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateFilters({ is_priority: val === '' ? undefined : val === 'true' });
                      }}
                    >
                      <option value="">Todos</option>
                      <option value="true">Somente prioritários</option>
                      <option value="false">Somente comuns</option>
                    </select>
                  </div>

                  {/* Empresa */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Empresa</label>
                    <select
                      className="input w-full text-sm"
                      value={filters.company_id !== undefined ? filters.company_id : ''}
                      onChange={e => {
                        const val = e.target.value;
                        updateFilters({ company_id: val === '' ? undefined : Number(val) });
                      }}
                    >
                      <option value="">Todas</option>
                      {companies.map(c => (
                        <option key={c.company_id} value={c.company_id}>{c.name} — {formatCnpj(c.document)}</option>
                      ))}
                    </select>
                  </div>

                  {/* Datas */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data de</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_from}
                        onChange={v => updateFilters({ date_from: v })}
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Data até</label>
                      <DateInputBR
                        className="input w-full text-sm"
                        value={filters.date_to}
                        onChange={v => updateFilters({ date_to: v })}
                      />
                    </div>
                  </div>

                  <button
                    className="text-xs text-emerald-600 hover:underline"
                    onClick={() => { setFilters({ status: undefined, is_active: undefined, is_priority: undefined, company_id: undefined, date_from: '', date_to: '' }); setCurrentPage(1); }}
                  >
                    Limpar filtros
                  </button>
                </div>
              )}
            </div>
            <div className="sm:w-80">
              <select
                className="input w-full"
                value={sort}
                onChange={e => changeSort(parsePurchaseOrderSort(e.target.value))}
                aria-label="Ordenar por"
                title="Ordenar por"
              >
                {PURCHASE_ORDER_SORT_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>
                    Ordenar: {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="mt-2 flex items-start gap-1.5 text-xs text-slate-500">
            <Info size={14} className="mt-0.5 shrink-0 text-slate-400" />
            <span>
              <span className="font-medium text-slate-600">Ordenação:</span> {purchaseOrderSortHint(sort)}
              {isOrderNumberSearch(searchTerm) && ' Na busca por número, o pedido com exatamente esse número aparece antes.'}
            </span>
          </p>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="animate-spin text-emerald-600" size={32} />
            </div>
          ) : orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
              <ShoppingCart size={48} className="mb-3" />
              <p className="text-lg font-medium">Nenhum pedido encontrado</p>
              <p className="text-sm mt-1">Ajuste os filtros ou crie um novo pedido.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Nº Pedido</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Data do pedido</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Empresa</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Fornecedor</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Referência</th>
                    <th className="py-3 px-4 text-left text-xs font-medium text-slate-500 uppercase">Situação</th>
                    <th className="py-3 px-4 text-right text-xs font-medium text-slate-500 uppercase">Total</th>
                    <th className="py-3 px-4 text-center text-xs font-medium text-slate-500 uppercase">Ações</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {orders.map(order => (
                    <TableRow key={order.purchase_order_id}>
                      <TableCell className="font-medium text-slate-900">
                        {order.order_number || order.purchase_order_id}
                      </TableCell>
                      <TableCell>
                        {formatDate(order.order_date)}
                        {order.created_at && (
                          <span className="block text-xs text-slate-400" title="Data e hora em que o pedido foi cadastrado no ERP">
                            criado {formatDateTimeBR(order.created_at)}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="text-slate-900">{order.company_name || '—'}</span>
                          {order.company_document && (
                            <span className="block text-xs text-slate-400">{formatCnpj(order.company_document)}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <span className="text-slate-900">{order.provider_name || '—'}</span>
                          {order.provider_document && (
                            <span className="block text-xs text-slate-400">{order.provider_document}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{order.purchase_order_reference || '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          {order.is_priority === true && (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              <AlertTriangle size={11} /> PRIORITÁRIO
                            </span>
                          )}
                          {getStatusBadge(order.status)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(order.total)}</TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <PermissionGuard permission="purchase_orders.read">
                            <button
                              className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                              title="Visualizar"
                              onClick={() => navigate(`/purchase-orders/${order.purchase_order_id}`)}
                            >
                              <Eye size={16} />
                            </button>
                          </PermissionGuard>
                          <PermissionGuard permission="purchase_orders.write">
                            <button
                              className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Editar"
                              onClick={() => navigate(`/purchase-orders/${order.purchase_order_id}/edit`)}
                            >
                              <Edit size={16} />
                            </button>
                          </PermissionGuard>
                          <PermissionGuard permission="purchase_orders.delete">
                            <button
                              className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Excluir"
                              onClick={() => setDeleteId(order.purchase_order_id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </PermissionGuard>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-1">
        <p className="text-sm text-slate-500 order-2 sm:order-1">
          {totalOrders > 0
            ? `${((currentPage - 1) * pageSize) + 1}–${Math.min(currentPage * pageSize, totalOrders)} de ${totalOrders} registro${totalOrders !== 1 ? 's' : ''} · Página ${currentPage} de ${totalPages}`
            : 'Nenhum registro'}
        </p>
        {totalPages > 1 && (
          <div className="flex items-center gap-1 order-1 sm:order-2">
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(1)}
              title="Primeira página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >«</button>
            <button
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage(p => p - 1)}
              title="Página anterior"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >‹</button>
            {getPageRange(currentPage, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ell-${i}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-sm">…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  title={`Página ${p}`}
                  className={`w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${
                    currentPage === p
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >{p}</button>
              )
            )}
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
              title="Próxima página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >›</button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              title="Última página"
              className="w-9 h-9 flex items-center justify-center rounded-md border text-sm font-medium border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >»</button>
          </div>
        )}
      </div>

      {/* Delete confirm */}
      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Excluir Pedido de Compra"
        message="Tem certeza que deseja excluir este pedido de compra? Esta ação não pode ser desfeita."
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
        loading={deleting}
      />
    </div>
  );
};
